import { Component, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { 
  BasePaginationController,
  ContextMenuClickInfo, 
  ContextMenuItem, 
  Explorer, 
  IAddButtonOptions, 
  IExplorerAttributes, 
  IExplorerContentAttributes, 
  IExplorerHeaderAttributes, 
  IExplorerNavAttributes, 
  IListView, 
  IListViewColHeader, 
  IListViewItem, 
  IPaginationReqMessage,
  OffCanvasIDsInDefaultLayout,
  Page,
  getOffCanvasIdFromLevel,
  OffCanvasPlacement,
  openOffCanvas, 
  OptimaOffCanvasService, 
  ToastService 
} from '@optima-stride-ai/optima-components';
import { 
  BizEntityFunctionTypes,
  BizEntityTypeCategory, 
  IBizEntity, 
  IBizEntityRelationships, 
  IBizEntityType, 
  ItxnRelationshipLog, 
  SettingsService 
} from '@optima-stride-ai/core';


import { OffCanvasRightContentTypes } from '../../off-canvas/off-canvas-service/off-canvas-form.service';
import { FormStates, getOffCanvasComponentForBizEntityCategory, OffCanvasComponents } from '../../off-canvas/off-canvas-service/off-canvas-form.service';
import { OffcanvasService } from '@coreui/angular';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizEntityStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { RelationshipStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { EntityBottomOffCanvasService } from '@optima-stride-ai/optima-app-framework';
import { TxnLogStateStoreService } from '../../transaction-log/txn-log-state-store/txn-log-state-store.service';
import { UtilService } from '../../utils/util.service';
import { ExplorerPaginationConfig } from '../../utils/explorer-pagination.config';
import { refreshExplorer, refreshExplorerNav } from '../../utils/explorer-nav-selection';
import { ExplorerReveal, ExplorerRoutes } from '../../utils/explorer-reveal';


import { getColHeadersForTransactionLog, getListViewForMeasureLogs } from '../../transaction-log/txn-log-list-view';
import { AppStateService } from '../../state/app-state';
import { ContextMenuService } from '../../context-menu/context-menu';
import { getColHeadersForBizEntity } from '../../strategy/explorer-list-view';
import { getListViewForTxnEntities } from '../../transaction(Measure)/transaction-list-view';
import { OffCanvasCollectiveComponent } from "../../off-canvas/off-canvas-form/off-canvas-collective/off-canvas-collective.component";

@Component({
  selector: 'app-check-in-explorer',
  imports: [Explorer],
  templateUrl: './check-in-explorer.component.html',
  styleUrl: './check-in-explorer.component.sass',
})
export class CheckInExplorerComponent extends BasePaginationController<IBizEntity> implements OnInit, OnDestroy {

  // ===================== Component Properties & ViewChild =====================
  
  @ViewChild('checkInExplorer') explorerComponent!: Explorer;
  @Input('isInModal') isInModal: boolean = false;
  
  // ===================== Explorer Attributes Configuration =====================
  
  subHeaderAttributes: IExplorerHeaderAttributes = {
    search: true,
    filter: false,
    add: false,
    view: false,
    sideBar: true,
    addWithOptions: false,
    addButtonOptions: [],
    breadCrumbToggle: true,
    nextDisable: false,
    previouseDisable: false,
    appliedFilterCount: 0,
    graphView: false,
  };

  explorerNavAttributes: IExplorerNavAttributes = {
    data: [],
    isNavigable: false,
  };

  explorerContentAttributes: IExplorerContentAttributes = {
    data: [],
    listData: {
      title: '',
      colHeaders: [],
      items: [],
    },
    currentSelectedItem: '',
  };

  explorerAttributes: IExplorerAttributes = {
    data: [],
    subHeaderAttributes: this.subHeaderAttributes,
    explorerNavAttributes: this.explorerNavAttributes,
    explorerContentAttributes: this.explorerContentAttributes,
    isInModal: this.isInModal,
    contextMenuItems: []
  };
  
  dataRefreshedToken: string = '';
  subHeaderOptions: IAddButtonOptions[] = [];
  private contextMenuItems: ContextMenuItem[] = [];

  // ===================== Service Injections =====================
  
  private readonly appStateService = inject(AppStateService);
  private readonly offCanvasFormService = inject(OptimaOffCanvasService);
  private readonly offCanvasServiceRef = inject(OffcanvasService);
  private readonly toastService = inject(ToastService);
  private readonly settingsService = inject(SettingsService);
  private readonly bizEntityTypesStateStoresService = inject(BizEntityTypeStateStoreService);
  private readonly bizEntityStateStoreService = inject(BizEntityStateStoreService);
  private readonly relationshipStateStoreService = inject(RelationshipStateStoreService);
  private readonly txnLogStateStoreService = inject(TxnLogStateStoreService);
  private readonly utilService = inject(UtilService);
  private readonly contextMenuService = inject(ContextMenuService);
  private readonly entityBottomOffCanvasService = inject(EntityBottomOffCanvasService);

  // ===================== Data Properties =====================
  
  strategyWithTxnData: IBizEntity[] = [];
  bizEntities: IBizEntity[] = [];
  bizEntityTypes: IBizEntityType[] = [];
  txnLogs: ItxnRelationshipLog[] = [];
  transactionalEntities: IBizEntity[] = [];
  
  // ===================== State Flags & Tracking =====================
  
  private _isActiveComponent = false;
  private _selectedEntity: IBizEntity | null = null;
  private lastVisitedItemId = '';
  private allDataLoaded = false;
  isLoading = signal<boolean>(true);
  private parentSelectedForFloatingAddButton: IBizEntity | undefined;
  private relationships: IBizEntityRelationships[] = [];
  private strategyData: IBizEntity[] = [];
  private bizEntityData: IBizEntity[] = [];
  readonly contentItemCountPerPage = ExplorerPaginationConfig.ContentItemCountPerPage;
  private readonly txnLogTotalCount = new Map<string, number>();
  private contentRequestSequence = 0;
  private readonly explorerReveal = new ExplorerReveal({
    route: ExplorerRoutes.Checkins,
    state: () => this.appStateService,
    explorer: () => this.explorerComponent,
    entities: () => this.explorerAttributes.data,
    fetch: (ids, ancestorsOf) => this.bizEntityStateStoreService.getState(
      this.bizEntityTypesStateStoresService, undefined, ids, undefined, ancestorsOf),
    merge: entities => this.appendLoadedEntities(entities),
    openFolder: id => this.navItemClicked(id),
  });
  private readonly deletedEntityIds = new Set<string>();

  private dataLoadingFlags = {
    bizEntities: false,
    relationships: false
  };
  
  private dataLoadedOnce = {
    txnLogs: false
  };

  // ===================== Subscriptions =====================
  
  private bizEntityStoreSubscription: Subscription | undefined;
  private relationshipStoreSubscription: Subscription | undefined;
  private txnLogStoreSubscription: Subscription | undefined;
  private deleteCompletedSubscription: Subscription | undefined;

  // ===================== Constructor =====================
  
  constructor() {
    super();
    this.contextMenuService.onMenuClicked = () => {
      setTimeout(() => {
        if (this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.BizEntityForms) {
          this.openBizEntityFormInOffCanvas()
        } else if(this.appStateService.offCanvasRightContentType === OffCanvasRightContentTypes.DetailForm)
        {
          this.openDetailOffCanvas();
        }
        else {
          this.openOthersInRightOffCanvas()
        }
      })
    };
    
    this.contextMenuService.onPinToQuickActionClicked = () => {
      setTimeout(() => {
        this.addToQuickActionClicked();
      });
    };
  }

  // ===================== Lifecycle Hooks =====================

  ngOnInit(): void {
    this.subscribeToStates();
    if (!this.isInModal) {
      this.explorerReveal.watch();
    }
    this.fetchData(false);
  }

  ngOnDestroy(): void {
    this.unSubscribeFromStates();
    this.deletedEntityIds.clear();
  }

  private unSubscribeFromStates() {
    if (this.bizEntityStoreSubscription) {
      this.bizEntityStoreSubscription.unsubscribe();
    }
    if(this.relationshipStoreSubscription){
      this.relationshipStoreSubscription.unsubscribe();
    }
    if(this.txnLogStoreSubscription){
      this.txnLogStoreSubscription.unsubscribe();
    }
    this.deleteCompletedSubscription?.unsubscribe();
  }

  private subscribeToStates() {
    if(this.bizEntityStateStoreService.changed){
      this.bizEntityStoreSubscription = this.bizEntityStateStoreService.changed.subscribe(() => {
        this.fetchData(true);
        
        // Force rebuild of content list with fresh tags
        // Thi check is there because when the component is 
        // initialized, the currentSelectedItem is empty ad we don't want
        //  to call updateListData at that time as it is not necessary

        const currentSelectedItem = this.explorerContentAttributes.currentSelectedItem;
        if (currentSelectedItem) {
          this.updateListData(currentSelectedItem);
        }
      });
    }
    if(this.relationshipStateStoreService.changed){
      this.relationshipStoreSubscription = this.relationshipStateStoreService.changed.subscribe(() => {
        // Reset all dataLoadedOnce flags so all data is fetched fresh from APIs
        // since relationships can link any entity to any other entity
        this.dataLoadedOnce = {
          txnLogs: false
        };
        this.fetchData(true);
        this.ensureTxnLogsLoaded();
      });
    }
    if(this.txnLogStateStoreService.changed){
      this.txnLogStoreSubscription = this.txnLogStateStoreService.changed.subscribe(() => {
        this.fetchData(true);
      });
    }
    this.deleteCompletedSubscription = this.contextMenuService.deleteCompletedEvent?.subscribe((entity) => {
      this.removeDeletedEntityFromExplorer(entity);
    });
  }

  private fetchData(isStateStoreServiceStateChanged: boolean): void {
    this.isLoading.set(true);
    this.dataLoadingFlags.bizEntities = false;
    this.dataLoadingFlags.relationships = false;
    this.allDataLoaded = false;
    
    this.fetchBizEntityTypesAndRelationships(isStateStoreServiceStateChanged);
  }

  private fetchBizEntityTypesAndRelationships(isStateStoreServiceStateChanged: boolean): void {
    this.bizEntityTypesStateStoresService.getState()
      .then((data) => {
        if (data) {
          this.bizEntityTypes = data;
        }
        this.defineSubHeaderAttributes();
        
        return this.relationshipStateStoreService.getState(
          undefined, 
          this.bizEntityTypesStateStoresService, 
          this.bizEntityStateStoreService,
          undefined,true
        );
      })
      .then((data) => {
        if (data) {
          this.relationships = data;
          const allTransactionalLinkedEntites = this.relationships.map(
            (item) => item.to.map((to) => to.target.bizEntity as IBizEntity)
          );
          const flatTransactionalLinkedEntites = allTransactionalLinkedEntites
            .flat()
            .filter((item) => item.type.category === BizEntityTypeCategory.Transactional)
            .filter(item => !this.deletedEntityIds.has(item.id.toString()));
          this.strategyData = flatTransactionalLinkedEntites;
        }
        
        this.dataLoadingFlags.relationships = true;
        return this.fetchBizEntityExplorerData(isStateStoreServiceStateChanged);
      })
      .catch((error) => {
        console.error('Error fetching relationships:', error);
        this.dataLoadingFlags.relationships = true;
      });
  }

  private fetchBizEntityExplorerData(isStateStoreServiceStateChanged: boolean): void {
    Promise.all([
      this.fetchPaginationPage(undefined, this.contentItemCountPerPage, 0),
      this.loadRelationshipHierarchy()
    ])
      .then(([page, hierarchy]) => {
        this.paginationBuffer.setInitialPage('', page);
        this.appendLoadedEntities([...hierarchy, ...page.items]);
        
        this.dataLoadingFlags.bizEntities = true;
        this.checkAndSetupExplorer(isStateStoreServiceStateChanged);
      })
      .catch((error) => {
        console.error('Error fetching biz entities:', error);
        this.dataLoadingFlags.bizEntities = true;
      });
  }

  private async loadRelationshipHierarchy(): Promise<IBizEntity[]> {
    const ancestorPages = await Promise.all(this.strategyData.map((entity) =>
      this.bizEntityStateStoreService.getState(
        this.bizEntityTypesStateStoresService,
        undefined,
        undefined,
        undefined,
        entity.id
      )
    ));
    const entitiesById = new Map<string, IBizEntity>();
    [...ancestorPages.flatMap((page) => page ?? []), ...this.getRelationshipHierarchySnapshot()]
      .forEach((entity) => entitiesById.set(entity.id, entity));
    return Array.from(entitiesById.values())
      .filter(entity => !this.deletedEntityIds.has(entity.id.toString()));
  }

  /**
   * Lazy load transaction logs only when needed
   * Call this before accessing txnLogs data for a specific transactional entity
   */
  private ensureTxnLogsLoaded(bizEntityId?: string, limit = this.contentItemCountPerPage, offset = 0, requestSequence?: number): Promise<void> {
    if (this.dataLoadedOnce.txnLogs && !bizEntityId) {
      return Promise.resolve();
    }
    
    return this.txnLogStateStoreService.getState(
      this.bizEntityStateStoreService, 
      this.bizEntityTypesStateStoresService,
      bizEntityId,
      limit,
      offset
    )
      .then((data) => {
        if (requestSequence !== undefined && requestSequence !== this.contentRequestSequence) return;
        if (data) {
          const logsById = new Map(this.txnLogs.map((log) => [log.id, log]));
          data.forEach((log) => logsById.set(log.id, log));
          this.txnLogs = Array.from(logsById.values());
          const totalRecords = (data[0] as (ItxnRelationshipLog & { totalRecords?: unknown }) | undefined)?.totalRecords;
          if (bizEntityId) {
            this.txnLogTotalCount.set(
              bizEntityId,
              typeof totalRecords === 'number' ? totalRecords : offset + data.length
            );
          }
          this.populateTxnLogWithTxnEntityData();
          this.dataLoadedOnce.txnLogs = !bizEntityId;
        }
      })
      .catch((error) => {
        console.error('Error loading txn logs:', error);
      });
  }

  private checkAndSetupExplorer(isStateStoreServiceStateChanged: boolean): void {
    if (this.dataLoadingFlags.bizEntities && 
        this.dataLoadingFlags.relationships && 
        !this.allDataLoaded) {
      this.allDataLoaded = true;
      this.setupExplorerOnInit(isStateStoreServiceStateChanged);
    }
  }
  
  getContextMenuItem(): void {
    this.contextMenuItems = this.contextMenuService.getMenu();
    this.explorerAttributes.contextMenuItems = this.contextMenuItems;
  }

  setupExplorerOnInit(isStateStoreServiceStateChanged: boolean): void {
    this.mergeStrategyAndTxnEntityData();
    this.defineExplorerAttributes(isStateStoreServiceStateChanged);
    this.getContextMenuItem();
    this.isLoading.set(false);
  }

  private mergeStrategyAndTxnEntityData(): void {
    this.populateTxnLogWithTxnEntityData();
    const bizEntityStoreEntireHierarchy = this.utilService.findEntireHierarchy(
      this.strategyData,
      this.bizEntityData,
      true
    );
    this.strategyWithTxnData = [...bizEntityStoreEntireHierarchy];
  }

  private populateTxnLogWithTxnEntityData(): void {
    if (!this.txnLogs.length || !this.bizEntityData.length) {
      return;
    }
    
    this.txnLogs.forEach((txnLog) => {
      const bizEntity = this.bizEntityData.find((entity) => entity.id === txnLog.bizEntity?.id);
      if (bizEntity) {
        txnLog.bizEntity = bizEntity;
      }
    });
  }

  private prepareAddButtonOptions(): void {
    const transactionalTypes = this.bizEntityTypes.filter(
      (item) => item.category === BizEntityTypeCategory.Transactional
    );
    
    this.subHeaderOptions = transactionalTypes.map((item) => ({
      id: item.id,
      label: item.name,
      icon: item.icon
    }));
  }


  onSubHeaderAddButtonClicked(): void {
    this.appStateService.selectedBizEntityForEdit = null;
    
    const entityType = this.bizEntityTypes.find(
      (item) => item.category === BizEntityTypeCategory.Transactional && item.isSystemType
    );
    
    if (!entityType) {
      console.error('No system transactional entity type found');
      return;
    }
    
    this.appStateService.selectedBizEntityTypeForNewBizEntity = entityType;
    this.appStateService.entityFormState = FormStates.New;
    
    setTimeout(() => {
      this.appStateService.entityFormState = FormStates.Idle;
    });
  }

  onAddButtonOptionClick = (option: IAddButtonOptions): void => {
    if (this.appStateService.entityFormState !== FormStates.Idle) {
      console.error('Entity form state is not idle. Cannot proceed with new entity creation.');
      return;
    }
    
    const entityType = this.bizEntityTypes.find((item) => item.id === option.id);
    if (!entityType) {
      console.error(`Entity type not found for option ID: ${option.id}`);
      return;
    }

    const offCanvasComponent = getOffCanvasComponentForBizEntityCategory(entityType);
    if (!offCanvasComponent) {
      console.error(`No off-canvas component found for entity type: ${entityType.name}`);
      return;
    }

    this.appStateService.selectedBizEntityForEdit = null;
    this.appStateService.selectedBizEntityTypeForNewBizEntity = entityType;
    this.appStateService.entityFormState = FormStates.New;
    this.openBizEntityFormInOffCanvas();
  }

  clickFilterButton = (): void => {
    // TODO: Implement filter functionality
  }

  private defineExplorerAttributes(
    isStateStoreServiceStateChanged: boolean = false,
    data: IBizEntity[] = this.strategyWithTxnData
  ): void {
    const revealId = isStateStoreServiceStateChanged || this.isInModal ? undefined : this.explorerReveal.takeInitialRequest();
    this.explorerAttributes.data = data;
    this.explorerAttributes.isInModal = this.isInModal;
    this.explorerAttributes.subHeaderAttributes = this.subHeaderAttributes;
    this.explorerAttributes.explorerNavAttributes = this.explorerNavAttributes;
    this.explorerAttributes.explorerContentAttributes = this.explorerContentAttributes;

    this.defineSubHeaderAttributes();
    this.defineExplorerNavAttributes();
    refreshExplorer(this);

    const preSelectedItemId = this.appStateService.incomingBizEntitySelectionId;
    const rootItemId = this.explorerAttributes.data.find((item) => !item.parentId)?.id || '';
    const targetItemId = isStateStoreServiceStateChanged
      ? this.explorerAttributes.explorerNavAttributes?.currentSelectedItem || rootItemId
      : preSelectedItemId || rootItemId;
    if (targetItemId) void this.updateListData(targetItemId);
    if (
      preSelectedItemId &&
      this.explorerAttributes.data &&
      this.explorerAttributes.data.length > 0
    ) {
      this.explorerAttributes.preSelectedItemId = preSelectedItemId;
    }
    if (revealId) {
      void this.explorerReveal.reveal(revealId);
    }
  }


  private defineSubHeaderAttributes(): void {
    this.prepareAddButtonOptions();
    
    if (this.isInModal) {
      this.subHeaderAttributes.add = false;
      this.subHeaderAttributes.addWithOptions = false;
      this.subHeaderAttributes.filter = false;
      this.subHeaderAttributes.graphView = false;
    }
    
    this.subHeaderAttributes.addButtonOptions = this.subHeaderOptions;
  }


  private defineExplorerNavAttributes(): void {
    this.explorerNavAttributes.data = [...this.explorerAttributes.data];
    
    if (!this.explorerNavAttributes.data.some((item) => item.parentId === '')) {
      this.explorerNavAttributes.data = this.utilService.findAncestors(
        this.explorerNavAttributes.data, 
        this.strategyWithTxnData
      );
    }
  }

  updateListData = (itemId: string): Promise<void> => {
    const requestSequence = ++this.contentRequestSequence;
    this.explorerNavAttributes.currentSelectedItem = itemId;
    this.explorerContentAttributes.currentSelectedItem = itemId;
    const item = this.explorerAttributes.data.find((entity) => entity.id === itemId);
    if (item?.type.category === BizEntityTypeCategory.Transactional) {
      this.txnLogs = [];
      return this.ensureTxnLogsLoaded(itemId, this.contentItemCountPerPage, 0, requestSequence)
        .then(() => {
          if (requestSequence !== this.contentRequestSequence) return;
          return this.defineExplorerContentAttributes(itemId);
        });
    }
    return this.fetchPaginationPage(itemId, this.contentItemCountPerPage, 0)
      .then(async (page) => {
        await this.explorerReveal.extendPage(itemId, page,
          offset => this.fetchPaginationPage(itemId, this.contentItemCountPerPage, offset),
          () => requestSequence === this.contentRequestSequence);
        if (requestSequence !== this.contentRequestSequence) return;
        this.paginationBuffer.setInitialPage(itemId, page);
        this.appendLoadedEntities(page.items);
        return this.defineExplorerContentAttributes(itemId);
      });
  }

  getContentTotalCount(): number {
    const itemId = this.explorerContentAttributes.currentSelectedItem || '';
    const item = this.explorerAttributes.data.find((entity) => entity.id === itemId);
    return item?.type.category === BizEntityTypeCategory.Transactional
      ? this.txnLogTotalCount.get(itemId) ?? this.txnLogs.length
      : this.getPaginationTotalCount(itemId, this.explorerContentAttributes.data.length);
  }

  override async onPaginationRequest(request: IPaginationReqMessage): Promise<void> {
    const isNavRequest = request.requesterId.startsWith('explorer-nav-');
    const itemId = this.explorerContentAttributes.currentSelectedItem || '';
    const item = this.explorerAttributes.data.find((entity) => entity.id === itemId);
    if (!isNavRequest && item?.type.category === BizEntityTypeCategory.Transactional) {
      const loadedCount = this.txnLogs.filter((log) => log.bizEntity.id === itemId).length;
      if (loadedCount >= this.getContentTotalCount()) return;
      // Content-only pagination: keep the nav tree's loading state untouched so scrolling
      // the list doesn't blank the hierarchy with a full-tree skeleton.
      this.explorerNavAttributes.isLoading = false;
      this.setPaginationLoading(true);
      try {
        const requestSequence = this.contentRequestSequence;
        await this.ensureTxnLogsLoaded(itemId, request.itemCountPerPage, loadedCount, requestSequence);
        if (requestSequence !== this.contentRequestSequence) return;
        await this.defineExplorerContentAttributes(itemId);
      } finally {
        this.setPaginationLoading(false);
      }
      return;
    }
    this.explorerNavAttributes.isLoading = isNavRequest;
    try {
      await super.onPaginationRequest(request);
    } finally {
      this.explorerNavAttributes.isLoading = false;
    }
  }

  protected override resolvePaginationParentId(request: IPaginationReqMessage): string {
    return request.parentId || this.explorerContentAttributes.currentSelectedItem || '';
  }

  protected override setPaginationLoading(isLoading: boolean): void {
    this.isLoading.set(isLoading);
  }

  protected override async fetchPaginationPage(parentId: string | undefined, limit: number, offset: number): Promise<Page<IBizEntity>> {
    const hierarchy = this.getRelationshipHierarchySnapshot();
    const hierarchyIds = new Set(hierarchy.map((entity) => entity.id));
    const candidates = hierarchy.filter((entity) => parentId
      ? entity.parentId === parentId
      : !entity.parentId || !hierarchyIds.has(entity.parentId)
    );
    const ids = candidates.slice(offset, offset + limit).map((entity) => entity.id);
    if (ids.length === 0) return { items: [], totalCount: candidates.length };
    const data = await this.bizEntityStateStoreService.getState(this.bizEntityTypesStateStoresService, undefined, ids);
    const items = (data ?? []).filter(entity => !this.deletedEntityIds.has(entity.id.toString()));
    return { items, totalCount: candidates.length };
  }

  protected override onNavPaginationChildrenLoaded(_parentId: string, appendedItems: IBizEntity[], _knownEmptyFolderIds: string[]): void {
    this.appendLoadedEntities(appendedItems);
  }

  protected override async onContentPaginationChildrenLoaded(parentId: string, appendedItems: IBizEntity[]): Promise<void> {
    if (parentId !== this.explorerContentAttributes.currentSelectedItem) return;
    this.appendLoadedEntities(appendedItems);
    await this.defineExplorerContentAttributes(parentId);
  }

  private appendLoadedEntities(entities: IBizEntity[]): void {
    entities = entities.filter(entity => !this.deletedEntityIds.has(entity.id.toString()));
    if (entities.length === 0) return;
    const entitiesById = new Map(this.bizEntityData.map((entity) => [entity.id, entity]));
    entities.forEach((entity) => entitiesById.set(entity.id, entity));
    this.bizEntityData = Array.from(entitiesById.values());
    this.mergeStrategyAndTxnEntityData();
    this.explorerAttributes.data = this.strategyWithTxnData;
    this.explorerNavAttributes.data = [...this.strategyWithTxnData];
    refreshExplorerNav(this.explorerComponent);
  }

  private removeDeletedEntityFromExplorer(entity: IBizEntity): void {
    const deletedId = entity.id.toString();
    this.deletedEntityIds.add(deletedId);
    const keepEntity = <T extends IBizEntity>(items: T[]): T[] =>
      items.filter(item => item.id.toString() !== deletedId);

    this.bizEntities = keepEntity(this.bizEntities);
    this.bizEntityData = keepEntity(this.bizEntityData);
    this.strategyData = keepEntity(this.strategyData);
    this.strategyWithTxnData = keepEntity(this.strategyWithTxnData);
    this.transactionalEntities = keepEntity(this.transactionalEntities);
    this.explorerAttributes.data = keepEntity(this.explorerAttributes.data);
    this.explorerNavAttributes.data = keepEntity(this.explorerNavAttributes.data);
    this.explorerContentAttributes.data = keepEntity(this.explorerContentAttributes.data);
    this.explorerContentAttributes.listData.items = this.explorerContentAttributes.listData.items
      .filter(item => String(item.id) !== deletedId);

    const parentId = entity.parentId?.toString() ?? '';
    const bufferedItems = this.paginationBuffer.getItems(parentId);
    if (bufferedItems.some(item => item.id.toString() === deletedId)) {
      const totalCount = this.paginationBuffer.getTotalCount(parentId) ?? bufferedItems.length;
      this.paginationBuffer.setInitialPage(parentId, {
        items: keepEntity(bufferedItems),
        totalCount: Math.max(0, totalCount - 1),
      });
    }

    refreshExplorerNav(this.explorerComponent);
    this.explorerComponent?.interceptListActions(this.explorerContentAttributes.listData);
  }

  private getRelationshipHierarchySnapshot(): IBizEntity[] {
    const entitiesById = new Map<string, IBizEntity>();
    this.strategyData.forEach((entity) => {
      entity.parentPath?.forEach((ancestor) => entitiesById.set(ancestor.id, ancestor));
      entitiesById.set(entity.id, entity);
    });
    return Array.from(entitiesById.values());
  }

  // Returns a promise so callers can await colHeaders/items being updated together, avoiding a mismatched intermediate render.
  private defineExplorerContentAttributes(
    itemId: string | null = this.explorerAttributes.data.find(
      (item) => item.parentId === '' || item.parentId === null
    )?.id || ''
  ): Promise<void> {
    if (!itemId) {
      return Promise.resolve();
    }
    
    this.explorerContentAttributes.data = [...this.explorerAttributes.data];
    const item = this.explorerContentAttributes.data.find((entity) => entity.id === itemId);
    
    if (item && item.type.category === BizEntityTypeCategory.Transactional) {
      const bizEntity = this.bizEntityData.find((entity) => entity.id === itemId);
      if (!bizEntity) {
        console.error(`Biz entity not found for transactional item: ${itemId}`);
        return Promise.resolve();
      }
      
      return this.prepareListDataAsync(itemId)
        .then((items) => {
          this.explorerContentAttributes.listData = {
            title: '',
            colHeaders: getColHeadersForTransactionLog(bizEntity),
            items
          };
          
          if (!this.isInModal) {
            this.setFloatingButtonAction(this.explorerContentAttributes.listData);
          }
          
          this.explorerComponent?.interceptListActions(this.explorerContentAttributes.listData);
        })
        .catch((error) => {
          console.error('Error loading transactional entity data:', error);
        });
    } else {
      const children = this.paginationBuffer.getItems(itemId);
      this.explorerContentAttributes.data = children;
      
      this.explorerContentAttributes.listData = {
        title: '',
        colHeaders: getColHeadersForBizEntity(false),
        items: this.prepareListData(itemId, children)
      };
      
      this.explorerComponent?.interceptListActions(this.explorerContentAttributes.listData);
      return Promise.resolve();
    }
  }

  private setFloatingButtonAction(listData: IListView<IBizEntity>): void {
    const isTransactional = this.parentSelectedForFloatingAddButton?.type.category === BizEntityTypeCategory.Transactional;
    const actionDisabled = !isTransactional;
    const floatingButtonLabel = 'Add Transaction Log';
    const floatingButtonToolTip = 'Add Log';
    
    if (!listData.headerActions) {
      listData.headerActions = [];
    }
    
    const existingAction = listData.headerActions.find(
      (action) => action.label === floatingButtonLabel && action.isFloating
    );
    
    if (existingAction) {
      existingAction.isDisabled = actionDisabled;
    } else {
      listData.headerActions.push({
        label: floatingButtonLabel,
        icon: 'cil-plus',
        action: this.onFloatButtonClicked,
        isFloating: true,
        tooltip: floatingButtonToolTip,
        isDisabled: actionDisabled
      });
    }
  }

  /**
   * Async wrapper to ensure required data is loaded before preparing list
   * Only loads txn logs when viewing a transactional entity
   */
  private prepareListDataAsync(itemId: string): Promise<IListViewItem<IBizEntity>[]> {
    const item = this.explorerAttributes.data.find(entity => entity.id === itemId);
    this.parentSelectedForFloatingAddButton = item;
    
    if (item && item.type.category === BizEntityTypeCategory.Transactional) {
      // Lazy load transaction logs only when viewing a transactional entity
      return this.ensureTxnLogsLoaded(itemId)
        .then(() => {
          const selectedEntityChildren = this.txnLogs.filter(
            (entity) => entity.bizEntity.id === itemId
          );
          return getListViewForMeasureLogs(
            selectedEntityChildren, 
            this.txnLogItemclicked, 
            this.selectClickedItem, 
            this.isInModal, 
            this.utilService
          ) as unknown as IListViewItem<IBizEntity>[];
        });
    } else {
      // For non-transactional entities, just show children (no API call needed)
      const selectedEntityChildren = this.extractItemChildren(itemId);
      const listItems = getListViewForTxnEntities(
        selectedEntityChildren, 
        this.menuButtonClicked, 
        this.itemClicked, 
        this.onAddButtonClicked, 
        this.selectClickedItem, 
        this.isInModal
      ) as unknown as IListViewItem<IBizEntity>[];
      
      return Promise.resolve(listItems);
    }
  }

  private prepareListData(itemId: string, children?: IBizEntity[]): IListViewItem<IBizEntity>[] {
    // This is a synchronous version for initial setup
    // Returns empty array if async data not loaded yet
    const item = this.explorerAttributes.data.find(entity => entity.id === itemId);
    this.parentSelectedForFloatingAddButton = item;
    
    if (item && item.type.category === BizEntityTypeCategory.Transactional) {
      if (!this.dataLoadedOnce.txnLogs) {
        return [];
      }
      const selectedEntityChildren = this.txnLogs.filter(
        (entity) => entity.bizEntity.id === itemId
      );
      return getListViewForMeasureLogs(
        selectedEntityChildren, 
        this.txnLogItemclicked, 
        this.selectClickedItem, 
        this.isInModal, 
        this.utilService
      ) as unknown as IListViewItem<IBizEntity>[];
    } else {
      const selectedEntityChildren = children ?? this.extractItemChildren(itemId);
      return getListViewForTxnEntities(
        selectedEntityChildren, 
        this.menuButtonClicked, 
        this.itemClicked, 
        this.onAddButtonClicked, 
        this.selectClickedItem, 
        this.isInModal
      ) as unknown as IListViewItem<IBizEntity>[];
    }
  }


  private menuButtonClicked = (item: IListViewItem<IBizEntity> | undefined, event?: Event) => { }
  _selectedBizEntity: IBizEntity | undefined;

  selectClickedItem = (itemId: string): void => {
    let item = this.bizEntityData.find((entity) => entity.id === itemId);
    
    if (!item) {
      item = this.strategyWithTxnData.find((entity) => entity.id === itemId);
    }
    
    if (item) {
      this.parentSelectedForFloatingAddButton = item;
      
      if (this.isInModal) {
        this._selectedBizEntity = item as IBizEntity;
      }
    }
  }

  get data(): IBizEntity | undefined {
    return this._selectedBizEntity;
  }

  navItemClicked = (itemId: string): void => {
    this.isLoading.set(true);
    this.explorerNavAttributes.isLoading = false;
    
    let item = this.bizEntityData.find((entity) => entity.id === itemId);
    
    if (!item) {
      item = this.strategyWithTxnData.find((entity) => entity.id === itemId);
    }
    
    if (item) {
      this.parentSelectedForFloatingAddButton = item;
      
      if (this.isInModal) {
        this._selectedBizEntity = item as IBizEntity;
      }
    }
    
    this.updateListData(itemId)
      .then(() => {
        this.isLoading.set(false);
        this.explorerNavAttributes.isLoading = false;
      })
      .catch((error) => {
        console.error('Error updating list data:', error);
        this.isLoading.set(false);
        this.explorerNavAttributes.isLoading = false;
      });
    this.resetIncomingBizEntitySelectionId();
  }

  itemClicked = (itemId: string, actionOnOperationalElementClicked: boolean = true): void => {
    this.subHeaderAttributes.previouseDisable = false;
    
    let item = this.explorerAttributes.data.find((entity) => entity.id === itemId);
    
    if (!item) {
      const txnLog = this.txnLogs.find((log) => log.id.toString() === itemId.toString());
      if (txnLog) {
        this.onActionButtonClicked(txnLog.id.toString());
        return;
      }
    }

    if (item?.type) {
      this.isLoading.set(true);
      
      this.updateListData(itemId)
        .then(() => {
          this.isLoading.set(false);
        })
        .catch((error) => {
          console.error('Error updating list data:', error);
          this.isLoading.set(false);
        });
    } else if (!this.isInModal && item?.type.functionType === BizEntityFunctionTypes.Operational) {
      this.onActionButtonClicked(itemId);
    }

    this.resetIncomingBizEntitySelectionId();
  }
  txnLogItemclicked = (itemId: string): void => {
    const txnLog = this.txnLogs.find((log) => log.id.toString() === itemId.toString());
    
    if (!txnLog) {
      console.error(`Transaction log not found for ID: ${itemId}`);
      return;
    }
    
    this.appStateService.entityFormState = FormStates.Edit;
    this.appStateService.currentSelectedTxnLogItem = txnLog;
    this.appStateService.offCanvasDetailActiveFor = txnLog;

    // ItxnRelationshipLog isn't an IBizEntity, so this path stays on the legacy bottom off-canvas
    // (EntityBottomOffCanvasService only accepts biz entities).
    openOffCanvas(
      this.offCanvasFormService,
      this.offCanvasServiceRef,
      OffCanvasComponents.TxnLogDetail,
      getOffCanvasIdFromLevel((this.offCanvasFormService.currentOffCanvas?.level ?? 0) + 1, OffCanvasPlacement.Bottom),
      (this.offCanvasFormService.currentOffCanvas?.level ?? 0) + 1,
      true
    );

    setTimeout(() => {
      this.appStateService.entityFormState = FormStates.Idle;
    });
  }
  onFloatButtonClicked = (item: IListViewItem<IBizEntity> | undefined, event?: Event): void => {
    const bizEntity = this.bizEntityData.find(
      (entity) => entity.id === this.parentSelectedForFloatingAddButton?.id
    );

    if (!bizEntity) {
      this.toastService.toast('No biz entity is selected to add log to!');
      return;
    }

    this.appStateService.setSelectedTransaction(bizEntity as IBizEntity);
    this.appStateService.entityFormState = FormStates.New;
    
    openOffCanvas(
      this.offCanvasFormService,
      this.offCanvasServiceRef,
      OffCanvasComponents.TxnLog,
      getOffCanvasIdFromLevel((this.offCanvasFormService.currentOffCanvas?.level ?? 0) + 1),
      (this.offCanvasFormService.currentOffCanvas?.level ?? 0) + 1
    );
    
    this.appStateService.entityFormState = FormStates.Idle;
  }

  onAddButtonClicked = (item: IListViewItem<IBizEntity> | undefined, event?: Event): void => {
    if (!item?.id) {
      console.error('No item ID provided for adding transaction log');
      return;
    }
    
    const bizEntity = this.bizEntityData.find((entity) => entity.id === item.id);

    if (!bizEntity) {
      console.error(`Biz entity not found for ID: ${item.id}`);
      return;
    }
    
    this.appStateService.setSelectedTransaction(bizEntity as IBizEntity);
    this.appStateService.entityFormState = FormStates.New;
    
    openOffCanvas(
      this.offCanvasFormService,
      this.offCanvasServiceRef,
      OffCanvasComponents.TxnLog,
      getOffCanvasIdFromLevel((this.offCanvasFormService.currentOffCanvas?.level ?? 0) + 1),
      (this.offCanvasFormService.currentOffCanvas?.level ?? 0) + 1
    );
    
    this.appStateService.entityFormState = FormStates.Idle;
  }

  processContentContextMenuClicked(event: ContextMenuClickInfo): void {
    const { id: itemId, event: domEvent } = event;
    
    if (!itemId) {
      console.error('Invalid item ID for context menu');
      return;
    }

    let item = this.bizEntityData.find((entity) => entity.id === itemId)
      ?? this.explorerAttributes.data.find((entity) => entity.id === itemId);
    
    if (!item) {
      console.error(`Item not found for ID: ${itemId}`);
      return;
    }

    this.appStateService.selectedBizEntityForEdit = item as IBizEntity;
    this.appStateService.selectedBizEntityTypeForNewBizEntity = item.type;
    
    const bizEntity = this.bizEntityData.find((entity) => entity.id === itemId);
    if (bizEntity) {
      this.appStateService.setSelectedTransaction(bizEntity as IBizEntity);
    }
    
    if (item.type.category === BizEntityTypeCategory.Transactional && bizEntity) {
      this.appStateService.setSelectedTransaction(bizEntity as IBizEntity);
    }
    
    this.contextMenuService.bizEnitityEvent.emit(item);
    this.updateContextMenuList(item, [...this.contextMenuItems]);
  }

  updateContextMenuList(entity: IBizEntity, menuItems: ContextMenuItem[]): void {
    const list = this.contextMenuService.getEntityListBasedOnType(
      entity,
      menuItems
    );
    this.explorerAttributes.contextMenuItems = list;

    // When the "Cross App" option is present, fetch the related entities for each
    // cross-app relationship target type asynchronously and reassign a NEW array
    // reference so an already-open context menu re-renders with the populated
    // sub-items.
    this.contextMenuService.populateCrossAppSubItems(entity, list).then(updatedList => {
      if (updatedList) {
        this.explorerAttributes.contextMenuItems = updatedList;
      }
    });
  }

  private extractItemChildren(itemId: string): IBizEntity[] {
    return this.utilService.extractItemChildren(itemId, this.strategyWithTxnData) as IBizEntity[];
  }

  onActionButtonClicked(itemId: string): void {
    const item = this.bizEntityData.find((entity) => entity.id === itemId)
      ?? this.strategyWithTxnData.find((entity) => entity.id === itemId);
    
    if (!item) {
      console.error(`Entity not found for ID: ${itemId}`);
      return;
    }
    
    this.appStateService.entityFormState = FormStates.Edit;
    this.appStateService.selectedBizEntityForEdit = item as IBizEntity;
    this.appStateService.selectedBizEntityTypeForNewBizEntity = item.type;
    this.openBizEntityFormInOffCanvas();
  }

  private openBizEntityFormInOffCanvas = (): void => {
    let component: OffCanvasComponents | null = null;
    let shouldOpen = false;
    
    if (this.appStateService.entityFormState === FormStates.New) {
      const entityType = this.appStateService.selectedBizEntityTypeForNewBizEntity;
      component = getOffCanvasComponentForBizEntityCategory(entityType || undefined);
      
      if (component) {
        shouldOpen = true;
      } else {
        console.error('[NEW] No off-canvas component found for the selected biz entity type');
      }
    } else if (
      this.appStateService.entityFormState === FormStates.Edit &&
      this.appStateService.selectedBizEntityForEdit
    ) {
      const entityType = this.appStateService.selectedBizEntityForEdit.type;
      component = getOffCanvasComponentForBizEntityCategory(entityType || undefined);
      console.log(this.appStateService.selectedBizEntityForEdit , 'selectedBizEntityForEdit');
      if (component) {
        shouldOpen = true;
      } else {
        console.error(`[EDIT] No off-canvas component found for ${entityType?.name}`);
      }
    }
    
    if (shouldOpen && component) {
      openOffCanvas(
        this.offCanvasFormService,
        this.offCanvasServiceRef,
        component,
      getOffCanvasIdFromLevel((this.offCanvasFormService.currentOffCanvas?.level ?? 0) + 1),
        (this.offCanvasFormService.currentOffCanvas?.level ?? 0) + 1,
        true
      );
    } else {
      this.appStateService.entityFormState = FormStates.Idle;
    }
  }

  private openOthersInRightOffCanvas = (): void => {
    if (this.appStateService.offCanvasRightContentType !== OffCanvasRightContentTypes.Others) {
      return;
    }
    
    openOffCanvas(
      this.offCanvasFormService,
      this.offCanvasServiceRef,
      this.appStateService.offCanvasRightComponent,
      getOffCanvasIdFromLevel((this.offCanvasFormService.currentOffCanvas?.level ?? 0) + 1),
      (this.offCanvasFormService.currentOffCanvas?.level ?? 0) + 1,
      false
    );
  }

  private openDetailOffCanvas = (): void => {
    if (this.appStateService.offCanvasRightContentType !== OffCanvasRightContentTypes.DetailForm) {
      return;
    }
    
    // Prefer the local enriched record; fall back to the selected entity so View works for
    // nodes visible in this explorer's tree that aren't in strategyWithTxnData (e.g. Groups).
    const entity = this.strategyWithTxnData.find(
      (item) => item.id === this.appStateService.selectedBizEntityForEdit?.id
    ) ?? this.appStateService.selectedBizEntityForEdit;

    if (!entity?.type) {
      console.error('Entity or entity type not found for detail off-canvas');
      // Reset so entityFormState doesn't stay stuck after the bailout.
      this.appStateService.entityFormState = FormStates.Idle;
      return;
    }

    this.appStateService.offCanvasDetailActiveFor = entity;
    this.entityBottomOffCanvasService.open(entity);
  }

  addToQuickActionClicked(): void {
    // TODO: Implement quick action functionality
  }

  resetIncomingBizEntitySelectionId(): void {
    this.appStateService.incomingBizEntitySelectionId = null;
  }
}
